import React, { useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import { Camera } from "lucide-react";
import corruptedImg from "../assets/images/corrupted-img.png";
import "../assets/css/coupleFormModal.css";

const retreatTitle = (row) => {
  const language = row?.language
    ? ` (${String(row.language).charAt(0).toUpperCase()}${String(
        row.language,
      ).slice(1)})`
    : "";
  const location = row?.location
    ? ` ${String(row.location).charAt(0).toUpperCase()}${String(
        row.location,
      ).slice(1)}`
    : "";
  return `${row?.title || row?.parish || "Retreat"}${language}${location}`.trim();
};

const retreatDates = (row) => {
  if (!row?.dateFrom) return "";
  if (row.dateFrom === row.dateTo) {
    return moment(row.dateFrom).format("MMM DD, YYYY");
  }
  return `${moment(row.dateFrom).format("MMM DD")} – ${moment(row.dateTo).format("MMM DD, YYYY")}`;
};

const photoUrl = (row) => {
  const url = row?.roasterImage || row?.imageUrl || "";
  if (!url || url === "null") return "";
  return url;
};

const fileName = (url) => {
  if (!url || url === "null") return "";
  return String(url).split("?")[0].split("/").pop();
};

const RetreatPhotoPicker = ({
  retreatRows = [],
  selectedUrl,
  couplePhotoUrl = "",
  onSelect,
  disabled = false,
  coupleId = null,
  onChangeRetreatPhoto = null,
  changingRosterId = null,
}) => {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const pendingRosterRef = useRef(null);
  const photos = (Array.isArray(retreatRows) ? retreatRows : []).filter(
    (row) => photoUrl(row),
  );
  const coupleUrl =
    typeof couplePhotoUrl === "string" &&
    couplePhotoUrl &&
    couplePhotoUrl !== "null"
      ? couplePhotoUrl
      : "";
  const coupleAlreadyListed = photos.some(
    (row) => fileName(photoUrl(row)) && fileName(photoUrl(row)) === fileName(coupleUrl),
  );
  const cards = [];
  if (coupleUrl && !coupleAlreadyListed) {
    cards.push({
      key: "couple-photo",
      url: coupleUrl,
      title: "Couple photo",
      dates: "Your last couple photo",
      isCouple: true,
      rosterId: null,
      coupleId: null,
    });
  }
  photos.forEach((row, index) => {
    cards.push({
      key: `${row.rosterId || row.id || "retreat"}-${index}`,
      url: photoUrl(row),
      title: retreatTitle(row),
      dates: retreatDates(row),
      isCouple: false,
      rosterId: row.rosterId || null,
      coupleId: row.coupleId || coupleId || null,
    });
  });
  if (!cards.length || disabled) return null;

  const choose = (url) => {
    onSelect(url);
    setOpen(false);
  };

  const canChangeRetreatPhoto =
    typeof onChangeRetreatPhoto === "function" && Boolean(coupleId);

  const openRetreatFilePicker = (event, card) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canChangeRetreatPhoto || !card.rosterId || changingRosterId) return;
    pendingRosterRef.current = {
      rosterId: card.rosterId,
      coupleId: card.coupleId || coupleId,
    };
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleRetreatFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    const pending = pendingRosterRef.current;
    event.target.value = "";
    pendingRosterRef.current = null;
    if (!file || !pending?.rosterId) return;
    onChangeRetreatPhoto({
      file,
      rosterId: pending.rosterId,
      coupleId: pending.coupleId || coupleId,
    });
  };

  const selectedName = fileName(
    typeof selectedUrl === "string" ? selectedUrl : "",
  );

  return (
    <div className="rr-retreat-photo-picker">
      <button
        type="button"
        className="rr-couple-photo-btn rr-retreat-photo-open"
        onClick={() => setOpen(true)}
      >
        Choose from a Retreat
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="rr-retreat-photo-file-input"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleRetreatFileChange}
      />
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        centered
        size="lg"
        className="rr-retreat-photo-modal"
        backdropClassName="rr-retreat-photo-backdrop"
        enforceFocus={false}
        restoreFocus
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>Choose from a Retreat</Modal.Title>
            <p className="rr-retreat-photo-modal-lead">
              Tap a photo to preview it as the couple profile. Use the camera
              button under a Retreat Picture to replace only that Retreat
              photo. Other Retreat Pictures and the couple profile stay
              unchanged until you Save.
            </p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="rr-retreat-photo-picker-list mt-3">
            {cards.map((card) => {
              const selected =
                (selectedName && fileName(card.url) === selectedName) ||
                (selectedUrl && String(selectedUrl) === String(card.url));
              const isChanging =
                changingRosterId &&
                String(changingRosterId) === String(card.rosterId);
              return (
                <div
                  key={card.key}
                  className={`rr-retreat-photo-option${
                    selected ? " is-selected" : ""
                  }${card.isCouple ? " is-couple-photo" : ""}`}
                >
                  <button
                    type="button"
                    className="rr-retreat-photo-select"
                    onClick={() => choose(card.url)}
                  >
                    <span className="rr-retreat-photo-thumb">
                      <img
                        src={card.url}
                        alt={card.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = corruptedImg;
                        }}
                      />
                      {selected ? (
                        <em className="rr-retreat-photo-check">Selected</em>
                      ) : null}
                    </span>
                    <span>{card.title}</span>
                    {card.dates ? <em>{card.dates}</em> : null}
                  </button>
                  {!card.isCouple && canChangeRetreatPhoto && card.rosterId ? (
                    <button
                      type="button"
                      className="rr-retreat-photo-change"
                      onClick={(event) => openRetreatFilePicker(event, card)}
                      disabled={Boolean(changingRosterId)}
                      title="Change this Retreat Picture"
                      aria-label={`Change Photo for ${card.title}`}
                    >
                      <Camera size={14} aria-hidden="true" />
                      <span>{isChanging ? "Uploading…" : "Change Photo"}</span>
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="light"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RetreatPhotoPicker;
